import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { CoachCard } from "./coaches/CoachCard";
import { CoachFilters } from "./coaches/CoachFilters";
import { CoachDetails } from "./coaches/CoachDetails";

type SortOption = "default" | "name" | "price_asc" | "price_desc";
type FilterType = "SAY" | "EA" | "SÖZ" | "DİL" | "all";
type RankRange = "all" | "-100" | "100-1000" | "1000-5000" | "5000-10000" | "+10K";
type FieldType = "all" | "SAY" | "EA" | "SÖZ" | "DİL";

interface CoachesListProps {
  title?: string;
  showFilters?: boolean;
  maxCoaches?: number;
  className?: string;
  onCoachSelect?: (coach: any) => void;
}

const fetchCoaches = async () => {
  console.log("Fetching coaches...");
  const { data, error } = await supabase.rpc('get_public_coach_info');

  if (error) {
    console.error("Error fetching coaches:", error);
    throw error;
  }
  
  console.log("Fetched coaches:", data);
  return data;
};

const getRankRangeValues = (range: RankRange): [number, number] => {
  switch (range) {
    case "-100":
      return [0, 100];
    case "100-1000":
      return [100, 1000];
    case "1000-5000":
      return [1000, 5000];
    case "5000-10000":
      return [5000, 10000];
    case "+10K":
      return [10000, 100000];
    default:
      return [0, 100000];
  }
};

export const CoachesList = ({ 
  title = "Koçlarımız", 
  showFilters = true, 
  maxCoaches,
  className = "",
  onCoachSelect
}: CoachesListProps) => {
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("default");
  const [selectedUniversities, setSelectedUniversities] = useState<string[]>([]);
  const [selectedRankRange, setSelectedRankRange] = useState<RankRange>("all");
  const [selectedCoach, setSelectedCoach] = useState<any | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [isUniversityExpanded, setIsUniversityExpanded] = useState(true);
  const [isRankExpanded, setIsRankExpanded] = useState(true);
  const [isFieldExpanded, setIsFieldExpanded] = useState(true);
  const [selectedField, setSelectedField] = useState<FieldType>("all");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [showFreeTrialOnly, setShowFreeTrialOnly] = useState(false);

  const { data: coaches = [], isLoading } = useQuery({
    queryKey: ["coaches"],
    queryFn: fetchCoaches,
    gcTime: 30 * 60 * 1000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const universities = useMemo(() => {
    const uniqueUniversities = [...new Set(coaches.map(coach => coach.university))];
    return uniqueUniversities.sort();
  }, [coaches]);

  const filteredAndSortedCoaches = coaches
    .filter((coach) => {
      const matchesSearch =
        coach.name_surname.toLowerCase().includes(searchQuery.toLowerCase()) ||
        coach.university.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesField = 
        selectedField === "all" || 
        (coach.placement_types && coach.placement_types.includes(selectedField));
      
      const matchesUniversity = 
        selectedUniversities.length === 0 || 
        selectedUniversities.includes(coach.university);

      const matchesFreeTrial = 
        !showFreeTrialOnly || 
        coach.free_trial === true;
      
      let matchesRank = false;
      if (selectedRankRange === "all") {
        matchesRank = true;
      } else if (coach.placement_ranks && coach.placement_types) {
        const [minRank, maxRank] = getRankRangeValues(selectedRankRange);
        matchesRank = coach.placement_ranks.some((rank: string, index: number) => {
          if (selectedField === "all" || coach.placement_types[index] === selectedField) {
            const rankNum = parseInt(rank);
            return rankNum >= minRank && rankNum <= maxRank;
          }
          return false;
        });
      }

      return matchesSearch && matchesField && matchesUniversity && matchesRank && matchesFreeTrial;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name_surname.localeCompare(b.name_surname);
        case "price_asc":
          return (a.monthly_price || 0) - (b.monthly_price || 0);
        case "price_desc":
          return (b.monthly_price || 0) - (a.monthly_price || 0);
        case "default":
        default:
          return 0;
      }
    })
    .slice(0, maxCoaches);

  return (
    <div className="bg-background min-h-screen w-full mt-16 pt-8">
      <div className="container mx-auto px-2 sm:px-4">
        {title && <h1 className="text-4xl font-bold text-center mb-8">{title}</h1>}

        <div className="flex flex-col md:flex-row gap-8">
          {showFilters && (
            <>
              {/* Desktop Filters */}
              <div className="hidden md:block w-64 space-y-6 px-2 md:px-0">
                <div className="bg-[#F1F0FB] rounded-lg p-6">
                  <CoachFilters 
                    universities={universities}
                    selectedUniversities={selectedUniversities}
                    onUniversityChange={setSelectedUniversities}
                    selectedRankRange={selectedRankRange}
                    onRankRangeChange={setSelectedRankRange}
                    selectedField={selectedField}
                    onFieldChange={setSelectedField}
                    isUniversityExpanded={isUniversityExpanded}
                    onUniversityExpandToggle={() => setIsUniversityExpanded(!isUniversityExpanded)}
                    isRankExpanded={isRankExpanded}
                    onRankExpandToggle={() => setIsRankExpanded(!isRankExpanded)}
                    isFieldExpanded={isFieldExpanded}
                    onFieldExpandToggle={() => setIsFieldExpanded(!isFieldExpanded)}
                    showFreeTrialOnly={showFreeTrialOnly}
                    onFreeTrialChange={setShowFreeTrialOnly}
                  />
                </div>
              </div>

              {/* Mobile Filters Sheet */}
              <Sheet open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
                <SheetTrigger asChild className="md:hidden fixed bottom-4 right-4 z-50">
                  <Button 
                    variant="default"
                    className="rounded-full shadow-lg bg-brand-primary hover:bg-brand-primary/90 text-white"
                  >
                    <SlidersHorizontal className="mr-2 h-4 w-4" />
                    Filtreler
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[300px] sm:w-[400px]">
                  <SheetHeader>
                    <SheetTitle>Filtreler</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <CoachFilters 
                      universities={universities}
                      selectedUniversities={selectedUniversities}
                      onUniversityChange={setSelectedUniversities}
                      selectedRankRange={selectedRankRange}
                      onRankRangeChange={setSelectedRankRange}
                      selectedField={selectedField}
                      onFieldChange={setSelectedField}
                      isUniversityExpanded={isUniversityExpanded}
                      onUniversityExpandToggle={() => setIsUniversityExpanded(!isUniversityExpanded)}
                      isRankExpanded={isRankExpanded}
                      onRankExpandToggle={() => setIsRankExpanded(!isRankExpanded)}
                      isFieldExpanded={isFieldExpanded}
                      onFieldExpandToggle={() => setIsFieldExpanded(!isFieldExpanded)}
                      showFreeTrialOnly={showFreeTrialOnly}
                      onFreeTrialChange={setShowFreeTrialOnly}
                    />
                  </div>
                </SheetContent>
              </Sheet>
            </>
          )}

          <div className="flex-1">
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Input
                placeholder="İsim veya üniversite ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="whitespace-nowrap">
                    Sırala
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => setSortBy("default")}>
                    Varsayılan
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy("name")}>
                    İsme Göre
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy("price_asc")}>
                    Fiyata Göre (Artan)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy("price_desc")}>
                    Fiyata Göre (Azalan)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className={`grid grid-cols-1 ${isMobile ? 'grid-cols-2 gap-3' : 'md:grid-cols-2 lg:grid-cols-3 gap-6'} px-2 sm:px-12 md:px-0`}>
              {filteredAndSortedCoaches.map((coach) => (
                <CoachCard
                  key={coach.id}
                  coach={coach}
                  onSelect={setSelectedCoach}
                  isMobile={isMobile}
                />
              ))}
            </div>

            {filteredAndSortedCoaches.length === 0 && !isLoading && (
              <div className="text-center py-12 text-muted-foreground">
                Arama kriterlerinize uygun koç bulunamadı.
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!selectedCoach} onOpenChange={() => setSelectedCoach(null)}>
        {selectedCoach && (
          <CoachDetails
            coach={selectedCoach}
            selectedPlan={selectedPlan}
            onPlanSelect={setSelectedPlan}
            onClose={() => setSelectedCoach(null)}
          />
        )}
      </Dialog>
    </div>
  );
};